import React, {PureComponent} from 'react';
import {Grid, Row, Col} from 'react-flexbox-grid';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';
import Papa from 'papaparse';
import BugsnagClient from '../../components/BugSnagClient';

import {getImportableQuestions} from '../../lib/form-helpers';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Input from '../../components/Input';
import FileInput from '../../components/FileUpload';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import { T } from '../../components/I18n';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';

class DataImportModal extends PureComponent {
  state = {
    allQuestions: [],
    headers: [],
    file: null,
    totalRows: 0,
    submitting: false,
    dateColumn: -1,
    importDateFormat: "yyyy-dd-MM HH:mm:ss",
    dateFormats: []
  }

  static getDerivedStateFromProps(props, state) {
    if (!props.form) {
      return ({
        allQuestions: [],
        headers: [],
        submitting: false,
        dateColumn: -1,
        file: null
      });
    }

    if (state.allQuestions.length === 0) {
      return ({allQuestions: getImportableQuestions(props.form).map(question => ({...question, vectors: vectorize(question.text)}))
      });
    }

    return null;
  }

  handleFilesAdded(files) {
    const {allQuestions} = this.state;

    Papa.parse(files[0].file, {
      worker: true,
      complete: (results, file) => {
        let headers = [];
        if (results.data[0]) {
          headers = results.data[0].map(item => item.replace(/[^a-zA-Z0-9_\s]/g, "").replace(/_/g, " ").replace(/\s+/g, " ")).map(item => ({item, vectors: vectorize(item)}));
        }
        this.setState({
          allQuestions: allQuestions.map(question => ({...question, prefered: findBestMatch(question.vectors, headers)})), 
          headers, 
          file: files[0],
          totalRows: results.data.length - 1
        });
      },
      error: (error, file) => {
        NotificationManager.error('Could not Parse Uploaded CSV File');
      }
    });
  }

  async scheduleImport() {
    const {credentials} = this.props;
    const {file} = this.state;
    this.setState({submitting:true});
    await api.uploadFile(file.file, file.type, credentials,
      (resource) => this.submitSchedule(resource), //onSuccess
      (reponse) => {
        BugsnagClient.notify(new Error('Failed to upload file to S3 for import'), (event) => {
          event.addMetadata("response", {reponse});
        });
        NotificationManager.error('Failed to upload the file');
      } //onFailure
    );
  }

  async submitSchedule(resource) {
    const {allQuestions, dateColumn, file, totalRows, importDateFormat} = this.state;
    const {form, credentials, onImportScheduled} = this.props;
    const columnMap = allQuestions.reduce((map, question) => {
      if (question.prefered.index > -1) {
        map[question.id] = question.prefered.index;
      }

      return map;
    }, {});
    const status = await api.scheduleImport(form, resource, file.file.name, dateColumn, importDateFormat,totalRows, columnMap, credentials);
    const updated = {...form, pendingImports: [...form.pendingImports, status]};
    onImportScheduled(updated, status);
  }

  updatePrefered(question, value) {
    const {allQuestions} = this.state;
    const updated = allQuestions.reduce((list, q) => {
      if (q.id === question.id) {
        q.prefered.index = value;
      }
      list.push(q);
      return list;
    }, []);

    this.setState({allQuestions: updated});
  }

  render() {
    const {form, dateFormats, onClose} = this.props;
    const {allQuestions, headers, dateColumn, submitting, file, importDateFormat} = this.state;
    if (!form) {
      return null;
    }

    return (
      <Form>
        <SideModal
          title={<T isolate>Import Data for <T ignore>{form.name}</T></T>}
          open={form}
          onClose={onClose}
          options={[
            <Button type="submit" onClick={() => this.scheduleImport()} disabled={!file || submitting}> Start Import </Button>,
            <Button secondary onClick={onClose}>Cancel Import</Button>
          ]}
          large
          >
            <SideModal.Content>
              <Form.Group>
                <FileInput id="upload_file" type="file" label="Select a file for upload" allowedTypes={["csv"]} onFilesAdd={(files) => this.handleFilesAdded(files)} disabled={submitting} maxMB={50}/>
              </Form.Group>
              {headers.length > 0 &&
                <Form.Group>
                  <Grid>
                    <RowContiner first>
                      <Row key={-1}>
                        <Col xs={5}>
                          Import date format
                        </Col>
                        <Col xs={2}>
                          <Icon name="arrow-right"/>
                        </Col>
                        <Col xs={5}>
                          <Input type="select" value={importDateFormat} onChange={(event) => this.setState({importDateFormat: event.target.value})} disabled={submitting}>
                            {dateFormats.sort((a,b) => a.order - b.order).map(dateFormat => (
                              <option key={dateFormat.format} value={dateFormat.format}>{dateFormat.example} - ({dateFormat.format})</option>
                            ))}
                          </Input>
                        </Col>
                      </Row>
                    </RowContiner>
                    <RowContiner>
                      <Row key={-1}>
                        <Col xs={5}>
                          Data submission date
                        </Col>
                        <Col xs={2}>
                          <Icon name="arrow-right"/>
                        </Col>
                        <Col xs={5}>
                          <Input type="select" value={dateColumn} onChange={(event) => this.setState({dateColumn: event.target.value})} disabled={submitting}>
                            <option value="-1">Not Imported</option>
                            {headers.map((header, index) => (
                              <option key={index} value={index}>{header.item}</option>
                            ))}
                          </Input>
                        </Col>
                      </Row>
                    </RowContiner>
                    {allQuestions
                      .sort((q1, q2) => q1.order - q2.order)
                      .map(question => 
                        (<ImportGroup key={question.id} question={question} inputs={headers} onChange={(question, value) => this.updatePrefered(question, value)} disabled={submitting}/>))
                    }
                  </Grid>
                </Form.Group>
              }
            </SideModal.Content>
        </SideModal>
      </Form>
    )
  }
}

function ImportGroup({id, question, inputs, onChange, disabled}) {
  return(
    <RowContiner>
      <Row key={id} style={{alignItems: "center"}}>
        <Col xs={5}>
          {question.text}
        </Col>
        <Col xs={2}>
          <Icon name="arrow-right"/>
        </Col>
        <Col xs={5}>
          <Input type="select" value={question.prefered.index} onChange={(event) => onChange(question, event.target.value)} disabled={disabled}>
            <option value="-1">Nothing Selected</option>
            {inputs.map((header, index) => (
              <option key={index} value={index}>{header.item}</option>
            ))}
          </Input>
        </Col>
      </Row>
    </RowContiner>
  );
}

function vectorize(string) {
  const wordList = string.toLowerCase().split(/\s/);
  const ngrams = {};
  for (let i = 0; i < wordList.length - 1; i++) {
    let key = wordList[i] + " " + wordList[i+1];
    let value = ngrams[key] ? ngrams[key] + 1 : 1;
    ngrams[key] = value;
  }

  return {ngrams, words: wordList};
}

//https://en.wikipedia.org/wiki/Cosine_similarity
function consineSimilarity(gram1, gram2) {
  //get all the unit vectors (keys) of the combined vector space
  const unitVectors = [...Object.keys(gram1), ...Object.keys(gram2)];
  //Calculate the dot product of the two grams
  const dotProduct = unitVectors.reduce((collector, key, index) => {
    let left = gram1[key] || 0;
    let right = gram2[key] || 0;
    collector += left * right;
    return collector;
  }, 0);

  //Calculate the magnitude of each vector
  const mag1 = Math.sqrt(Object.keys(gram1).reduce((sum, key, index) => {
    sum += gram1[key] * gram1[key];
    return sum
  },0));

  const mag2 = Math.sqrt(Object.keys(gram2).reduce((sum, key, index) => {
    sum += gram2[key] * gram2[key];
    return sum
  },0));

  //Calculate the cosine value
  return dotProduct / mag1 / mag2;
}

function findBestMatch(questionVector, headers) {
  return headers.reduce((solution, option, index) => {
    let score = 0;
    if (option.vectors.words.length > 1 && questionVector.words.length > 1) {
      score = consineSimilarity(questionVector.ngrams, option.vectors.ngrams);
    }
    else {
      if (option.vectors.words.length === 1) {
        score = questionVector.words.indexOf(option.vectors.words[0]) + 1;
      }
      else if (questionVector.words.length === 1) {
        score = option.vectors.words.indexOf(questionVector.words[0]) +1;
      }
    }

    if (score > solution.score) {
      return ({score, option, index});
    }
    return solution;

  }, {score: 0, option: null, index: -1});
}

const RowContiner = styled.div`
  padding-top: ${props => props.first ? 0 : "5px"};

  > .row{
    align-items: center
  }
`;

export default withUser(DataImportModal);